import { isNil } from 'lodash';
import { Frequency, Options as RRuleOptions } from 'rrule';
import { BatchScheduleComponentProps } from '@shared/scenes/entity/components/fields/DateEditField/types';
import {
  buildCustomRRule,
  getSharedOptions,
} from '@shared/scenes/entity/components/fields/DateEditField/utils';

interface UseBatchScheduleReturn {
  handleCountChange: (newCount: string) => void;
  handleFreqSelect: (newFreq: Frequency) => void;
  handleOffsetSelect: () => void;
  handlePresetSelect: (preset: Partial<RRuleOptions>, index: number) => void;
  handleCustomSelect: () => void;
}

export function useBatchSchedule({
  date,
  count,
  setCount,
  setPresetIndexSelected,
  setRruleOptions,
  setShowingCustomOptions,
  presets,
  setUserIsEditing,
  setOffset,
  setRecurrenceType,
}: Pick<
  BatchScheduleComponentProps,
  | 'date'
  | 'count'
  | 'setCount'
  | 'setPresetIndexSelected'
  | 'setRruleOptions'
  | 'presets'
  | 'setShowingCustomOptions'
  | 'setUserIsEditing'
  | 'setOffset'
  | 'setRecurrenceType'
>): UseBatchScheduleReturn {
  const handleCountChange = (newCount: string) => {
    if (newCount === '') {
      setCount?.(undefined);
      return;
    }
    const numCount = Number(newCount);
    const isNumber = !isNaN(numCount);
    if (isNumber) {
      setCount?.(numCount);
    }
    setUserIsEditing(true);
  };

  const handleFreqSelect = (newFreq: Frequency) => {
    setRecurrenceType('RRULE_SCHEDULE');
    setPresetIndexSelected(0);
    setRruleOptions(presets.filter(p => p.freq === newFreq)[0]);
    setUserIsEditing(true);
  };

  const handleOffsetSelect = () => {
    setRecurrenceType('OFFSET');
    setUserIsEditing(true);
    setCount?.(undefined);
    setPresetIndexSelected(-1);
    setShowingCustomOptions?.(false);
    // Clear rruleOptions so that switching back to custom/preset mode resets to defaults
    setRruleOptions(undefined);
    setOffset({
      unit: 'DAY',
      value: 1,
    });
  };

  const handlePresetSelect = (preset: Partial<RRuleOptions>, index: number) => {
    setRecurrenceType('RRULE_SCHEDULE');
    setPresetIndexSelected(index);
    setRruleOptions(preset);
    setUserIsEditing(true);
  };

  const handleCustomSelect = () => {
    setRecurrenceType('RRULE_SCHEDULE');
    setPresetIndexSelected(-1);
    setRruleOptions(prev => {
      if (!isNil(prev)) {
        return { ...prev, interval: prev.interval ?? 1 };
      }
      // When creating from scratch, default to weekly frequency
      return buildCustomRRule(getSharedOptions(date, count), {
        interval: 1,
        freq: Frequency.WEEKLY,
      });
    });
    setUserIsEditing(true);
    setShowingCustomOptions?.(true);
  };

  return {
    handleCountChange,
    handleFreqSelect,
    handleOffsetSelect,
    handlePresetSelect,
    handleCustomSelect,
  };
}
